/**
 * DragDropZone — full-width drag/drop upload affordance
 *
 * Dashed mint-border zone shown above the documents panel. Clicking
 * opens the file picker; dropping files invokes the same handler.
 *
 * Spec: /tmp/design-pass/borrower/specs/sign-and-documents.md (drag-drop zone)
 * - Visual-only in the original mock; this component wires real DnD events.
 *
 * Rules:
 * - Glass components only (rule 11b)
 * - sx + theme tokens, alpha() / baseColors.teal for mint border (rule 11c)
 * - All copy via t() (rule 13)
 * - 44px touch target on the dropzone itself (it's a button-role region)
 */

import UploadIcon from '@mui/icons-material/Upload';
import { alpha, Box, baseColors, iconSizes, Stack, Typography, useI18n } from '@ops-matrix/design-system-ui';
import { useCallback, useRef, useState } from 'react';

export interface DragDropZoneProps {
  /** Invoked when the user drops files or picks via the hidden input. */
  onFiles: (files: File[]) => void;
  /** Accept attr for the file input (defaults to common document/image types). */
  accept?: string;
  /** Allow multi-file drop / pick. Defaults to true. */
  multiple?: boolean;
}

export function DragDropZone({
  onFiles,
  accept = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif',
  multiple = true,
}: DragDropZoneProps) {
  const { t } = useI18n();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const openPicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
      if (files.length > 0) onFiles(files);
    },
    [onFiles],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files ? Array.from(e.target.files) : [];
      if (files.length > 0) onFiles(files);
      // Reset so the same file can be re-selected
      e.target.value = '';
    },
    [onFiles],
  );

  const borderColor = isDragging ? baseColors.teal[400] : alpha(baseColors.teal[400], 0.5);
  const bgColor = isDragging ? alpha(baseColors.teal[400], 0.12) : alpha(baseColors.teal[400], 0.04);

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={openPicker}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openPicker();
        }
      }}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      aria-label={t('consumer.documents.dropzoneTitle')}
      sx={{
        p: { xs: 3, md: 4.5 },
        borderRadius: 1,
        border: '1.5px dashed',
        borderColor,
        bgcolor: bgColor,
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        minHeight: 44,
        '&:hover': {
          borderColor: baseColors.teal[400],
          bgcolor: alpha(baseColors.teal[400], 0.08),
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'success.main',
          outlineOffset: 2,
        },
      }}
    >
      <input
        type="file"
        ref={inputRef}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
        multiple={multiple}
        accept={accept}
      />
      <Stack spacing={0.5} sx={{ alignItems: 'center' }}>
        <UploadIcon sx={{ fontSize: iconSizes['3xl'], color: 'success.main' }} aria-hidden="true" />
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {t('consumer.documents.dropzoneTitle')}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {t('consumer.documents.dropzoneSubtitle')}
        </Typography>
      </Stack>
    </Box>
  );
}

export default DragDropZone;
